import { FeaturesToPaywall, getPaywallPath } from '@helpers/paywall'
import type { Account } from '@models/Account'
import type { FeatureKey } from '@type/featureKeys'
import { FeatureState } from '@type/featureKeys'
import type { Feature } from '@type/features'

// Pure: no React, no queries. The hook form is
// @helpers/hooks/useFeatureAccess; keeping this module free of the query
// layer is what lets nav filters, landing routes and route guards import
// it without the api.ts → Routes module cycle.

/**
 * The one `/my-account` field every feature question reads. A narrow Pick
 * so pure callers (nav filters, landing routes, tests) can pass a minimal
 * object; a full Account satisfies it structurally.
 */
export type FeatureAccessUser = Pick<Account, 'features'>

export interface FeatureAccess {
  /**
   * The permissions (and any beta flag) admit this feature: the key is
   * present in the answer, whatever its state. What a ROUTE guard asks.
   */
  reachable: boolean
  /** Offer it: nav entries, landing candidates, dashboard buttons. */
  usable: boolean
  /** The plan lacks it: render the entry locked, link to the paywall. */
  locked: boolean
  /** Switched off in Settings → Features: nav drops it, URL still opens it. */
  hidden: boolean
  /** The plan feature whose paywall unlocks it, when `locked`. */
  paywall?: Feature
  paywallPath?: string
}

const DENIED: FeatureAccess = { reachable: false, usable: false, locked: false, hidden: false }

/**
 * How a feature resolved for the viewer, as the backend decided it.
 *
 * `undefined` means the account payload has not resolved (or predates the
 * field) and reads as DENIED. This is the one place the frontend fails
 * closed on an unknown: both shells block render until /my-account
 * resolves (AuthRoute / AuthLayout), so in practice the closed window is
 * the deploy skew between a backend without `features` and a frontend
 * that reads it — and a closed nav plus a closed route is a blank-but-safe
 * app, where an open nav against closed routes is a redirect loop.
 */
export const getFeatureAccess = (key: FeatureKey, user?: FeatureAccessUser | null): FeatureAccess => {
  const entry = user?.features?.[key]
  if (!entry) return DENIED
  const paywall = entry.state === FeatureState.locked ? entry.plan_feature : undefined
  const paywallFeature = paywall ? FeaturesToPaywall[paywall] : undefined
  return {
    reachable: true,
    usable: entry.state === FeatureState.usable,
    locked: entry.state === FeatureState.locked,
    hidden: entry.state === FeatureState.hidden,
    paywall,
    paywallPath: paywallFeature ? getPaywallPath(paywallFeature) : undefined,
  }
}

export const isFeatureUsable = (key: FeatureKey, user?: FeatureAccessUser | null): boolean =>
  getFeatureAccess(key, user).usable

export const isFeatureLocked = (key: FeatureKey, user?: FeatureAccessUser | null): boolean =>
  getFeatureAccess(key, user).locked

export const isFeatureReachable = (key: FeatureKey, user?: FeatureAccessUser | null): boolean =>
  getFeatureAccess(key, user).reachable
